import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getTemplateSections, trackTemplateUsage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, X, FileX, Loader2, Star, Clock, TrendingUp, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface Template {
  id: string;
  title: string;
  type: string;
  value: any;
  description?: string;
  tags?: string[] | string;
  usage_count?: number;
  last_used_at?: string;
  is_favorite?: boolean;
  created_at?: string;
  category?: string;
}

interface Props {
  open: boolean;
  type: string;
  onClose: () => void;
  onApply: (template: Template) => void;
}

type FilterType = 'all' | 'favorites' | 'personal' | 'clinic' | 'shared';

const TYPE_LABELS: Record<string, string> = {
  complaint: 'Complaints', medicine: 'Medicines', investigation: 'Investigations',
  advice: 'Advice', diagnosis: 'Diagnosis', quick_notes: 'Quick Notes'
};

const FILTERS: { value: FilterType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'favorites', label: 'Favorites' },
  { value: 'personal', label: 'Personal' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'shared', label: 'Shared' },
];

function parseTags(raw?: string[] | string | null): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  return raw.split(',').map(t => t.trim()).filter(Boolean);
}

function formatDate(d?: string) {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }); }
  catch { return null; }
}

function getPreview(template: Template): string {
  if (!template.value) return '';
  try {
    const v = typeof template.value === 'string' ? JSON.parse(template.value) : template.value;
    if (template.type === 'complaint' && Array.isArray(v))
      return v.filter(c => c.complaint).map(c => c.complaint).join(', ');
    if (template.type === 'medicine' && Array.isArray(v))
      return v.filter(m => m.brand_name || m.product).map(m => m.brand_name || m.product).join(', ');
    if (template.type === 'investigation' && Array.isArray(v))
      return v.join(', ');
    if (typeof v === 'string')
      return v.length > 100 ? v.slice(0, 100) + '…' : v;
  } catch { /* ignore */ }
  return '';
}

export default function LoadTemplateModal({ open, type, onClose, onApply }: Props) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [applying, setApplying] = useState<string | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!open) { setSearch(''); setFilter('all'); setTemplates([]); return; }
    (async () => {
      try {
        setLoading(true);
        const data = await getTemplateSections(
          type, currentUser?.doctor_id || '', currentUser?.clinic_id || '',
          { favorites_only: false, limit: 50 }
        );
        setTemplates(data?.templates ? data.templates : Array.isArray(data) ? data : []);
      } catch { setTemplates([]); }
      finally { setLoading(false); }
    })();
  }, [open, type, currentUser?.doctor_id, currentUser?.clinic_id]);

  const handleApply = async (template: Template) => {
    try {
      setApplying(template.id);
      if (currentUser?.doctor_id)
        await trackTemplateUsage(template.id, currentUser.doctor_id, 'prescription_form');
    } catch { /* non-critical */ }
    finally { setApplying(null); }
    onApply(template);
    onClose();
  };

  const filtered = React.useMemo(() => templates.filter(t => {
    if (search) {
      const q = search.toLowerCase();
      if (!t.title?.toLowerCase().includes(q) &&
          !t.description?.toLowerCase().includes(q) &&
          !parseTags(t.tags).some(tag => tag.toLowerCase().includes(q)))
        return false;
    }
    if (filter === 'favorites') return t.is_favorite;
    if (filter !== 'all') return t.category === filter;
    return true;
  }), [templates, search, filter]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl p-0 overflow-hidden flex flex-col gap-0 max-h-[80vh]">

        {/* Header */}
        <DialogHeader className="px-5 py-4 border-b border-border flex-shrink-0">
          <DialogTitle className="text-base font-semibold">
            Load {TYPE_LABELS[type] || 'Template'}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Select a saved template to apply
          </p>
        </DialogHeader>

        {/* Search + filters */}
        <div className="px-5 pt-3 pb-3 space-y-2.5 flex-shrink-0 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search templates…"
              className="pl-9 pr-8 h-9"
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex gap-1 flex-wrap">
            {FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium transition-colors",
                  filter === f.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {f.value === 'favorites' && <Star className="w-3 h-3 inline mr-1" />}
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-1.5 text-center">
              <FileX className="w-8 h-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                {search ? `No results for "${search}"` : 'No templates found'}
              </p>
            </div>
          ) : filtered.map(template => {
            const preview = getPreview(template);
            const tags = parseTags(template.tags);
            const isApplying = applying === template.id;

            return (
              <div
                key={template.id}
                onClick={() => !applying && handleApply(template)}
                className={cn(
                  "group flex items-start gap-3 px-4 py-3 rounded-lg border border-border",
                  "cursor-pointer transition-colors hover:bg-muted/50 hover:border-border",
                  applying && applying !== template.id && "opacity-40 pointer-events-none"
                )}
              >
                <div className="flex-1 min-w-0 space-y-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-sm truncate">{template.title}</span>
                    {template.is_favorite === true && (
                      <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                    )}
                  </div>
                  {template.description && (
                    <p className="text-xs text-muted-foreground truncate">{template.description}</p>
                  )}
                  {preview && (
                    <p className="text-xs text-muted-foreground/70 truncate">{preview}</p>
                  )}
                  {(tags.length > 0 || template.usage_count || template.last_used_at) && (
                    <div className="flex items-center gap-2 pt-1">
                      {template.usage_count != null && template.usage_count > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <TrendingUp className="w-3 h-3" />{template.usage_count}
                        </span>
                      )}
                      {template.last_used_at && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <Clock className="w-3 h-3" />{formatDate(template.last_used_at)}
                        </span>
                      )}
                      {tags.slice(0, 3).map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-normal">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={e => { e.stopPropagation(); handleApply(template); }}
                  disabled={!!applying}
                  className={cn(
                    "h-7 w-7 p-0 flex-shrink-0 transition-opacity mt-0.5",
                    isApplying ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                  )}
                >
                  {isApplying
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Check className="w-3.5 h-3.5" />
                  }
                </Button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-border flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-muted-foreground">
            {filtered.length} template{filtered.length !== 1 ? 's' : ''}
          </p>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 text-xs">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
